/**
 * Route Optimization Utilities
 * For finding optimal insertion points and route calculations
 */

import {
  ILatLng,
  ILocation,
  calculateDistanceBetweenPoints,
  calculateRouteDistance,
  locationToLatLng,
  estimateTravelTime,
} from "./haversine";

export interface IRouteStop {
  id: number;
  location: ILatLng;
  sequence: number;
}

export interface IRouteForCalculation {
  storeLocation: ILatLng;
  stops: IRouteStop[];
  totalDistance: number;
}

export interface IInsertionResult {
  index: number;
  additionalDistance: number;
  newTotalDistance: number;
}

export interface IRouteSegment {
  from: ILatLng;
  to: ILatLng;
  segmentIndex: number;
  distance: number;
}

export interface IPointToRouteResult {
  distanceToRoute: number;
  nearestSegmentIndex: number;
  nearestPointOnRoute: ILatLng;
  distanceFromStart: number;
}

export type ProximityLevel = "near" | "medium" | "far";

export interface IProximityThresholds {
  near: number; // km
  medium: number; // km
}

const DEFAULT_THRESHOLDS: IProximityThresholds = {
  near: 1, // < 1km
  medium: 3, // < 3km
};

/**
 * Find the optimal insertion point for a new order in an existing route
 * Tries all possible positions and returns the one with minimum additional distance
 * Includes return trip to store (round trip)
 */
export const findOptimalInsertionPoint = (
  orderLocation: ILatLng,
  route: IRouteForCalculation
): IInsertionResult => {
  const { storeLocation, stops } = route;

  // Build array of locations: [store, ...stops, store] for round trip
  const sortedStops = stops.sort((a, b) => a.sequence - b.sequence).map((s) => s.location);
  const locations: ILatLng[] = [storeLocation, ...sortedStops];

  // Add return to store if there are stops
  if (sortedStops.length > 0) {
    locations.push(storeLocation);
  }

  // Calculate current route distance
  const currentDistance = calculateRouteDistance(locations);

  let bestIndex = 1; // Default: insert after store
  let minAdditionalDistance = Infinity;

  // Try inserting at each position (after store, between stops, before return to store)
  // Don't insert after the final store location (which is the return point)
  const maxInsertIndex = sortedStops.length > 0 ? locations.length - 1 : locations.length;

  for (let insertIndex = 1; insertIndex <= maxInsertIndex; insertIndex++) {
    // Create new route with order inserted
    const newLocations = [
      ...locations.slice(0, insertIndex),
      orderLocation,
      ...locations.slice(insertIndex),
    ];

    const newDistance = calculateRouteDistance(newLocations);
    const additionalDistance = newDistance - currentDistance;

    if (additionalDistance < minAdditionalDistance) {
      minAdditionalDistance = additionalDistance;
      bestIndex = insertIndex;
    }
  }

  return {
    index: bestIndex,
    additionalDistance: minAdditionalDistance,
    newTotalDistance: currentDistance + minAdditionalDistance,
  };
};

/**
 * Calculate all possible insertion points with their impacts
 * Includes return trip to store (round trip)
 */
export const calculateAllInsertionOptions = (
  orderLocation: ILatLng,
  route: IRouteForCalculation
): Array<IInsertionResult & { description: string }> => {
  const { storeLocation, stops } = route;

  const sortedStops = stops.sort((a, b) => a.sequence - b.sequence).map((s) => s.location);
  const locations: ILatLng[] = [storeLocation, ...sortedStops];

  // Add return to store if there are stops
  if (sortedStops.length > 0) {
    locations.push(storeLocation);
  }

  const currentDistance = calculateRouteDistance(locations);
  const options: Array<IInsertionResult & { description: string }> = [];

  // Don't insert after the final store location (which is the return point)
  const maxInsertIndex = sortedStops.length > 0 ? locations.length - 1 : locations.length;

  for (let insertIndex = 1; insertIndex <= maxInsertIndex; insertIndex++) {
    const newLocations = [
      ...locations.slice(0, insertIndex),
      orderLocation,
      ...locations.slice(insertIndex),
    ];

    const newDistance = calculateRouteDistance(newLocations);
    const additionalDistance = newDistance - currentDistance;

    let description: string;
    if (insertIndex === 1) {
      description = "First stop after store";
    } else if (insertIndex === maxInsertIndex) {
      description = "Last stop before return";
    } else {
      description = `After stop #${insertIndex - 1}`;
    }

    options.push({
      index: insertIndex,
      additionalDistance,
      newTotalDistance: newDistance,
      description,
    });
  }

  return options.sort((a, b) => a.additionalDistance - b.additionalDistance);
};

/**
 * Project a point onto a line segment and return the nearest point
 */
const projectPointOntoSegment = (
  point: ILatLng,
  segmentStart: ILatLng,
  segmentEnd: ILatLng
): { point: ILatLng; t: number } => {
  const dx = segmentEnd.lng - segmentStart.lng;
  const dy = segmentEnd.lat - segmentStart.lat;

  if (dx === 0 && dy === 0) {
    return { point: segmentStart, t: 0 };
  }

  // Calculate projection parameter t (clamped to [0,1])
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.lng - segmentStart.lng) * dx +
        (point.lat - segmentStart.lat) * dy) /
        (dx * dx + dy * dy)
    )
  );

  return {
    point: {
      lat: segmentStart.lat + t * dy,
      lng: segmentStart.lng + t * dx,
    },
    t,
  };
};

/**
 * Find the nearest point on a route (polyline) to a given point
 */
export const findNearestPointOnRoute = (
  point: ILatLng,
  route: ILatLng[]
): IPointToRouteResult => {
  if (route.length === 0) {
    throw new Error("Route cannot be empty");
  }

  if (route.length === 1) {
    return {
      distanceToRoute: calculateDistanceBetweenPoints(point, route[0]),
      nearestSegmentIndex: 0,
      nearestPointOnRoute: route[0],
      distanceFromStart: 0,
    };
  }

  let minDistance = Infinity;
  let nearestPoint: ILatLng = route[0];
  let nearestSegmentIndex = 0;
  let distanceFromStart = 0;
  let cumulativeDistance = 0;

  for (let i = 0; i < route.length - 1; i++) {
    const segmentStart = route[i];
    const segmentEnd = route[i + 1];

    const projected = projectPointOntoSegment(point, segmentStart, segmentEnd);
    const distance = calculateDistanceBetweenPoints(point, projected.point);

    if (distance < minDistance) {
      minDistance = distance;
      nearestPoint = projected.point;
      nearestSegmentIndex = i;
      distanceFromStart =
        cumulativeDistance +
        calculateDistanceBetweenPoints(segmentStart, projected.point);
    }

    cumulativeDistance += calculateDistanceBetweenPoints(
      segmentStart,
      segmentEnd
    );
  }

  return {
    distanceToRoute: minDistance,
    nearestSegmentIndex,
    nearestPointOnRoute: nearestPoint,
    distanceFromStart,
  };
};

/**
 * Get all segments of a route with their distances
 */
export const getRouteSegments = (route: ILatLng[]): IRouteSegment[] => {
  const segments: IRouteSegment[] = [];

  for (let i = 0; i < route.length - 1; i++) {
    segments.push({
      from: route[i],
      to: route[i + 1],
      segmentIndex: i,
      distance: calculateDistanceBetweenPoints(route[i], route[i + 1]),
    });
  }

  return segments;
};

/**
 * Determine proximity level based on distance
 */
export const getProximityLevel = (
  distance: number,
  thresholds: IProximityThresholds = DEFAULT_THRESHOLDS
): ProximityLevel => {
  if (distance <= thresholds.near) return "near";
  if (distance <= thresholds.medium) return "medium";
  return "far";
};

/**
 * Calculate route impact when adding an order
 * Includes return trip to store (round trip)
 */
export const calculateRouteImpact = (
  orderLocation: ILatLng,
  route: IRouteForCalculation,
  insertionIndex: number,
  avgSpeedKmh: number = 30
): {
  additionalDistance: number;
  additionalTime: number;
  impactPercentage: number;
} => {
  const { storeLocation, stops, totalDistance } = route;

  const sortedStops = stops.sort((a, b) => a.sequence - b.sequence).map((s) => s.location);
  const locations: ILatLng[] = [storeLocation, ...sortedStops];

  // Add return to store if there are stops
  if (sortedStops.length > 0) {
    locations.push(storeLocation);
  }

  const newLocations = [
    ...locations.slice(0, insertionIndex),
    orderLocation,
    ...locations.slice(insertionIndex),
  ];

  const currentDistance = totalDistance || calculateRouteDistance(locations);
  const newDistance = calculateRouteDistance(newLocations);
  const additionalDistance = newDistance - currentDistance;
  const additionalTime = estimateTravelTime(additionalDistance, avgSpeedKmh);
  const impactPercentage = currentDistance > 0
    ? (additionalDistance / currentDistance) * 100
    : 0;

  return {
    additionalDistance,
    additionalTime,
    impactPercentage,
  };
};

/**
 * Reorder stops based on new sequence
 */
export const reorderStops = (
  stops: IRouteStop[],
  fromIndex: number,
  toIndex: number
): IRouteStop[] => {
  const result = [...stops].sort((a, b) => a.sequence - b.sequence);
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);

  // Update sequences
  return result.map((stop, index) => ({
    ...stop,
    sequence: index + 1,
  }));
};

/**
 * Calculate new distances after reordering
 * Includes return trip to store for the last stop
 */
export const calculateDistancesAfterReorder = (
  storeLocation: ILatLng,
  stops: IRouteStop[]
): Array<{ id: number; distanceToNext: number }> => {
  const sortedStops = [...stops].sort((a, b) => a.sequence - b.sequence);
  const result: Array<{ id: number; distanceToNext: number }> = [];

  // Distance from store to first stop
  if (sortedStops.length > 0) {
    const allPoints = [storeLocation, ...sortedStops.map((s) => s.location)];

    for (let i = 0; i < sortedStops.length; i++) {
      let distanceToNext: number;

      if (i < sortedStops.length - 1) {
        // Distance to next stop
        distanceToNext = calculateDistanceBetweenPoints(allPoints[i + 1], allPoints[i + 2]);
      } else {
        // Last stop: distance back to store (return trip)
        distanceToNext = calculateDistanceBetweenPoints(sortedStops[i].location, storeLocation);
      }

      result.push({
        id: sortedStops[i].id,
        distanceToNext,
      });
    }
  }

  return result;
};
